import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, MenuItem, Button, Alert,
  Chip, Divider, Link, LinearProgress, Stepper, Step, StepLabel,
} from '@mui/material';
import { ReceiptLong, CheckCircle, ContentCopy, OpenInNew } from '@mui/icons-material';
import api from '../../services/api';
import type { InvoicingConfig, RepresentativeInfo } from '../../types';
import TextField from '../common/TextField';

// Guía paso a paso con capturas de ARCA (la del facturador compartido); ?cuit=&nombre= muestra nuestro representante.
const GUIDE_URL = 'https://facturador-cnvtl.ondigitalocean.app/guia-arca';
const CONDICION_GUIA: Record<number, string> = { 1: 'responsable_inscripto', 6: 'monotributo', 4: 'exento' };

const CONDICION_EMISOR = [
  { value: 1, label: 'Responsable Inscripto' },
  { value: 6, label: 'Monotributo' },
  { value: 4, label: 'Exento' },
];

const ArcaSetupWizard: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const [rep, setRep] = useState<RepresentativeInfo | null>(null);
  const [cfg, setCfg] = useState<InvoicingConfig>({ puntoVenta: 1, condicionIvaEmisor: 1, defaultIvaRate: 21 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([api.getInvoicingRepresentative(), api.getInvoicingConfig()]);
        setRep(r);
        setCfg({ ...c, puntoVenta: c.puntoVenta || 1, defaultIvaRate: c.defaultIvaRate || 21 });
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const verified = !!cfg.delegationVerifiedAt;

  const save = async () => {
    setSaving(true); setError(null); setOkMsg(null);
    try {
      const upd = await api.saveInvoicingConfig(cfg);
      setCfg({ ...upd, puntoVenta: upd.puntoVenta || 1, defaultIvaRate: upd.defaultIvaRate || 21 });
      setOkMsg('Datos fiscales guardados.');
      onSaved?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setVerifying(true); setError(null); setOkMsg(null);
    try {
      await api.saveInvoicingConfig(cfg); // persistir antes de verificar
      const res = await api.verifyArcaConnection();
      if (res.ok) {
        setOkMsg(res.message || 'Conexión verificada.');
        const c = await api.getInvoicingConfig();
        setCfg({ ...c, puntoVenta: c.puntoVenta || 1, defaultIvaRate: c.defaultIvaRate || 21 });
      } else {
        setError(res.message || 'No se pudo verificar la conexión.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al verificar la conexión');
    } finally {
      setVerifying(false);
    }
  };

  const copyCuit = () => { if (rep?.representativeCuit) navigator.clipboard?.writeText(rep.representativeCuit); };

  const guideParams = new URLSearchParams();
  if (rep?.representativeCuit) guideParams.set('cuit', rep.representativeCuit);
  if (CONDICION_GUIA[cfg.condicionIvaEmisor]) guideParams.set('condicion', CONDICION_GUIA[cfg.condicionIvaEmisor]);
  const guideShareUrl = `${GUIDE_URL}?${guideParams}`;
  const guideEmbedUrl = `${GUIDE_URL}?embed=1&${guideParams}`;
  const copyGuide = () => { navigator.clipboard?.writeText(guideShareUrl); setOkMsg('Link del instructivo copiado: mandáselo a tu contador.'); };

  if (loading) return <Card sx={{ mt: 3 }}><CardContent><LinearProgress /></CardContent></Card>;

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <ReceiptLong color="primary" />
          <Typography variant="h6">Facturación electrónica (ARCA)</Typography>
          {verified
            ? <Chip size="small" color="success" icon={<CheckCircle />} label="Conectado" />
            : <Chip size="small" color="warning" label="Sin verificar" />}
          {rep && <Chip size="small" variant="outlined" label={rep.environment === 'production' ? 'Producción' : 'Homologación'} />}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Para facturar, hacé estos pasos en ARCA una sola vez. Tu sistema factura "en representación tuya"
          usando el certificado de la plataforma.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Button variant="outlined" size="small" onClick={() => setShowGuide(!showGuide)}>
            {showGuide ? 'Ocultar instructivo' : 'Ver instructivo paso a paso con capturas'}
          </Button>
          <Button size="small" startIcon={<ContentCopy />} onClick={copyGuide}>Copiar link para mi contador</Button>
        </Stack>
        {showGuide && (
          <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: '#fff' }}>
            <iframe src={guideEmbedUrl} title="Instructivo para conectar ARCA" style={{ width: '100%', height: 720, border: 0 }} loading="lazy" />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {okMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOkMsg(null)}>{okMsg}</Alert>}
        {rep && !rep.certificateConfigured && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            El certificado central de la plataforma todavía no está configurado en el servidor. Avisá al administrador.
          </Alert>
        )}

        <Stepper orientation="vertical" sx={{ mb: 2 }}>
          <Step active expanded>
            <StepLabel>Dar de alta un punto de venta "Web Service" en ARCA</StepLabel>
            <Box sx={{ pl: 4, pb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Ingresá a ARCA con tu Clave Fiscal → "Administración de puntos de venta y domicilios" → Alta de un
                punto de venta tipo <b>Factura Electrónica - Web Service</b>. Anotá el número y cargalo abajo.
              </Typography>
              <Link href="https://www.afip.gob.ar" target="_blank" rel="noopener" sx={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                Abrir ARCA <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Box>
          </Step>

          <Step active expanded>
            <StepLabel>Delegar el web service "Facturación Electrónica" a la plataforma</StepLabel>
            <Box sx={{ pl: 4, pb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                En ARCA → "Administrador de Relaciones de Clave Fiscal" → Nueva Relación → Buscar → ARCA →
                WebServices → <b>Facturación Electrónica</b>. En "Representante", ingresá este CUIT y confirmá:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Chip
                  label={rep?.representativeCuit ? `CUIT representante: ${rep.representativeCuit}` : 'CUIT representante no configurado'}
                  color={rep?.representativeCuit ? 'primary' : 'default'}
                />
                {rep?.representativeCuit && (
                  <Button size="small" startIcon={<ContentCopy />} onClick={copyCuit}>Copiar</Button>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Después nosotros aceptamos la autorización en ARCA (en el día hábil) y ya podés verificar la conexión.
              </Typography>
            </Box>
          </Step>

          <Step active expanded>
            <StepLabel>Cargar tus datos fiscales y verificar la conexión</StepLabel>
            <Box sx={{ pl: 4, pb: 1 }}>
              <Stack spacing={2} sx={{ maxWidth: 520 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="CUIT" fullWidth size="small"
                    value={cfg.cuit || ''}
                    onChange={e => setCfg({ ...cfg, cuit: e.target.value })}
                    placeholder="Sin guiones"
                  />
                  <TextField
                    label="Punto de venta" type="number" size="small" sx={{ width: 160 }}
                    value={cfg.puntoVenta}
                    onChange={e => setCfg({ ...cfg, puntoVenta: parseInt(e.target.value, 10) || 1 })}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select label="Condición frente al IVA" fullWidth size="small"
                    value={cfg.condicionIvaEmisor}
                    onChange={e => setCfg({ ...cfg, condicionIvaEmisor: Number(e.target.value) })}
                  >
                    {CONDICION_EMISOR.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  {cfg.condicionIvaEmisor === 6 ? (
                    <TextField
                      select label="Categoría" size="small" sx={{ width: 160 }}
                      value={cfg.monotributoCategory || ''}
                      onChange={e => setCfg({ ...cfg, monotributoCategory: e.target.value || null })}
                    >
                      <MenuItem value="">Sin cargar</MenuItem>
                      {'ABCDEFGHIJK'.split('').map(c => <MenuItem key={c} value={c}>Categoría {c}</MenuItem>)}
                    </TextField>
                  ) : (
                    <TextField
                      label="IVA por defecto (%)" type="number" size="small" sx={{ width: 160 }}
                      value={cfg.defaultIvaRate}
                      onChange={e => setCfg({ ...cfg, defaultIvaRate: Number(e.target.value) || 21 })}
                    />
                  )}
                </Stack>
                <TextField
                  label="Razón social (fiscal)" size="small"
                  value={cfg.razonSocialFiscal || ''}
                  onChange={e => setCfg({ ...cfg, razonSocialFiscal: e.target.value })}
                />
                <TextField
                  label="Domicilio fiscal" size="small"
                  value={cfg.domicilioFiscal || ''}
                  onChange={e => setCfg({ ...cfg, domicilioFiscal: e.target.value })}
                />

                <Divider />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="outlined" onClick={save} disabled={saving || verifying}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </Button>
                  <Button variant="contained" onClick={verify} disabled={verifying || saving || !cfg.cuit}>
                    {verifying ? 'Verificando…' : 'Verificar conexión'}
                  </Button>
                  {verified && (
                    <Typography variant="caption" color="success.main">
                      Verificado el {new Date(cfg.delegationVerifiedAt as string).toLocaleString()}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Step>
        </Stepper>
      </CardContent>
    </Card>
  );
};

export default ArcaSetupWizard;
